import React from 'react';
import type { ComparisonPaginationData } from '@app-types/comparison';
import { buildPaginationItems } from '@utils/pagination';
import './ComparisonPagination.less';

export interface ComparisonPaginationProps {
  /**
   * Legacy static items/selected. Only used when `currentPage`/`totalPages`
   * are not supplied, so older call sites keep rendering unchanged.
   */
  data?: ComparisonPaginationData;
  /** Current page (1-based). Enables the interactive mode together with `totalPages`. */
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  /**
   * When true (interactive mode only), page controls are disabled and page
   * changes are ignored while keeping the current page displayed. Used to stop
   * stacked requests while the selected page is still loading.
   */
  disabled?: boolean;
  nodeId?: string;
}

const PREV_SYMBOL = '«';
const NEXT_SYMBOL = '»';
const ELLIPSIS_SYMBOL = '...';

/**
 * Pagination row for the Comparison and Instructions screens.
 *
 * Interactive mode (`currentPage` + `totalPages`) renders prev/next controls
 * plus a dynamic page list such as `« 1 ... 5 6 7 ... 9 »`. Without those props
 * it falls back to the original display-only rendering of `data`.
 */
const ComparisonPagination: React.FC<ComparisonPaginationProps> = ({
  data,
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  nodeId,
}) => {
  const isInteractive = currentPage !== undefined && totalPages !== undefined;

  if (!isInteractive) {
    if (!data) {
      return null;
    }

    return (
      <nav className="comparison-pagination" aria-label="ページ送り" data-node-id={nodeId}>
        <ul className="comparison-pagination__list">
          {data.items.map((item, index) => {
            if (item === ELLIPSIS_SYMBOL) {
              return (
                <li
                  key={`ell-${index}`}
                  className="comparison-pagination__item comparison-pagination__item--ellipsis"
                  aria-hidden="true"
                >
                  {item}
                </li>
              );
            }

            const isSelected = item === data.selected;
            return (
              <li key={`p-${item}-${index}`}>
                <button
                  type="button"
                  className={`comparison-pagination__item${isSelected ? ' is-selected' : ''}`}
                  aria-current={isSelected ? 'page' : undefined}
                >
                  {item}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }

  const total = Math.max(1, totalPages as number);
  const page = Math.min(Math.max(currentPage as number, 1), total);
  const items = buildPaginationItems(page, total);

  const goToPage = (next: number) => {
    if (disabled || next < 1 || next > total || next === page) {
      return;
    }
    onPageChange?.(next);
  };

  return (
    <nav className="comparison-pagination" aria-label="ページ送り" data-node-id={nodeId}>
      <ul className="comparison-pagination__list">
        <li>
          <button
            type="button"
            className="comparison-pagination__item"
            aria-label="前のページ"
            disabled={disabled || page <= 1}
            onClick={() => goToPage(page - 1)}
          >
            {PREV_SYMBOL}
          </button>
        </li>

        {items.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <li
                key={`ell-${index}`}
                className="comparison-pagination__item comparison-pagination__item--ellipsis"
                aria-hidden="true"
              >
                {ELLIPSIS_SYMBOL}
              </li>
            );
          }

          const isSelected = item === page;
          return (
            <li key={`p-${item}`}>
              <button
                type="button"
                className={`comparison-pagination__item${isSelected ? ' is-selected' : ''}`}
                aria-current={isSelected ? 'page' : undefined}
                aria-label={`${item}ページ`}
                disabled={disabled}
                onClick={() => goToPage(item)}
              >
                {item}
              </button>
            </li>
          );
        })}

        <li>
          <button
            type="button"
            className="comparison-pagination__item"
            aria-label="次のページ"
            disabled={disabled || page >= total}
            onClick={() => goToPage(page + 1)}
          >
            {NEXT_SYMBOL}
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default ComparisonPagination;
